import CloseIcon from "@mui/icons-material/Close";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import type {InsightRankedGuestExclusionV4} from "../../models/crm-v4";

interface RankedGuestExclusionsDialogProps {
    open: boolean;
    listTitle: string;
    exclusions: InsightRankedGuestExclusionV4[];
    loading: boolean;
    updating: boolean;
    error: boolean;
    onRestore: (exclusion: InsightRankedGuestExclusionV4) => void;
    onClose: () => void;
}

export function RankedGuestExclusionsDialog({
    open,
    listTitle,
    exclusions,
    loading,
    updating,
    error,
    onRestore,
    onClose,
}: RankedGuestExclusionsDialogProps) {
    const {t} = useTranslation();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="ranked-guest-exclusions-title">
            <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 3, py: 2}}>
                <Typography id="ranked-guest-exclusions-title" component="h2" variant="h6" noWrap>
                    {t("Excluded profiles")} · {listTitle}
                </Typography>
                <IconButton aria-label={t("Close")} onClick={onClose} sx={{ml: "auto"}}>
                    <CloseIcon/>
                </IconButton>
            </Box>

            <DialogContent dividers sx={{p: 0, minHeight: 160}}>
                {error && (
                    <Alert severity="error" sx={{m: 2}}>
                        {t("Could not update the excluded profiles.")}
                    </Alert>
                )}
                {loading ? (
                    <Box sx={{display: "grid", placeItems: "center", minHeight: 160}}>
                        <CircularProgress size={28}/>
                    </Box>
                ) : exclusions.length === 0 ? (
                    <Box sx={{display: "grid", placeItems: "center", minHeight: 160, px: 3}}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {t("No profiles are excluded from this list.")}
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {exclusions.map(exclusion => (
                            <ListItem
                                key={exclusion.profileId}
                                divider
                                secondaryAction={(
                                    <Button
                                        color="inherit"
                                        startIcon={<RestoreOutlinedIcon/>}
                                        disabled={updating}
                                        onClick={() => onRestore(exclusion)}
                                        sx={{textTransform: "none"}}
                                    >
                                        {t("Show again")}
                                    </Button>
                                )}
                                sx={{py: 1.5, pr: 16}}
                            >
                                <ListItemText
                                    primary={exclusion.name}
                                    primaryTypographyProps={{variant: "body1", fontWeight: 600, noWrap: true}}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button color="inherit" onClick={onClose} sx={{textTransform: "none"}}>
                    {t("Close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default RankedGuestExclusionsDialog;
